import React, { useState } from 'react';
import axios from 'axios';
import { useNavigate } from 'react-router-dom';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Textarea } from '@/components/ui/textarea';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card';
import { toast } from 'sonner';
import { Send, Star } from 'lucide-react';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const PublicFeedbackForm = () => {
  const navigate = useNavigate();
  const [formData, setFormData] = useState({
    service_type: '',
    customer_name_location: '',
    location: '',
    rating: 0,
    what_we_did_well: '',
    where_to_improve: '',
    customer_name: '',
    customer_email: '',
    signature: ''
  });
  const [loading, setLoading] = useState(false);
  const [submitted, setSubmitted] = useState(false);

  const ratingOptions = [
    { value: 1, label: 'Completely Dissatisfied', labelEs: 'Completamente Insatisfecho', color: '#EF4444', emoji: '😞' },
    { value: 2, label: 'Needs Improvement', labelEs: 'Necesita Mejorar', color: '#F59E0B', emoji: '😕' },
    { value: 3, label: 'Average', labelEs: 'Promedio', color: '#EAB308', emoji: '😐' },
    { value: 4, label: 'Very Good', labelEs: 'Muy Bueno', color: '#3B82F6', emoji: '😊' },
    { value: 5, label: 'Excellent', labelEs: 'Excelente', color: '#6B9F3E', emoji: '🤩' }
  ];

  const serviceTypes = [
    'Janitorial / Limpieza',
    'Landscaping / Jardinería',
    'Maintenance / Mantenimiento',
    'Event Support / Apoyo de Eventos',
    'Special Projects / Proyectos Especiales'
  ];

  const handleSubmit = async (e) => {
    e.preventDefault();
    
    if (formData.rating === 0) {
      toast.error('Please select a rating / Por favor seleccione una calificación');
      return;
    }

    setLoading(true);

    try {
      await axios.post(`${API}/customer-feedback`, formData);
      toast.success('Thank you for your feedback! / ¡Gracias por su opinión!');
      setSubmitted(true);
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to submit feedback / Error al enviar');
    } finally {
      setLoading(false);
    }
  };

  const getRatingInfo = (rating) => {
    return ratingOptions.find(opt => opt.value === rating);
  };

  if (submitted) {
    return (
      <div className="min-h-screen flex items-center justify-center p-4" style={{ background: 'linear-gradient(135deg, #E0F2F1 0%, #B2DFDB 100%)' }}>
        <Card className="w-full max-w-2xl shadow-2xl text-center">
          <CardContent className="py-12">
            <div className="text-6xl mb-4">✓</div>
            <h2 className="text-3xl font-bold mb-4" style={{ fontFamily: 'Space Grotesk, sans-serif', color: '#6B9F3E' }}>
              Thank You! / ¡Gracias!
            </h2>
            <p className="text-lg text-slate-700 mb-2">
              Your feedback has been submitted successfully.
            </p>
            <p className="text-lg text-slate-700 mb-6">
              Su opinión ha sido enviada exitosamente.
            </p>
            <p className="text-sm text-slate-600">
              We appreciate you taking the time to help us improve our services.
            </p>
            <p className="text-sm text-slate-600 mb-6">
              Agradecemos que se haya tomado el tiempo para ayudarnos a mejorar nuestros servicios.
            </p>
            <img 
              src="https://customer-assets.emergentagent.com/job_tfs-workspace/artifacts/xuj2h70u_TFSI%203-26%20PNG.png" 
              alt="TFS Logo" 
              className="h-16 mx-auto"
            />
          </CardContent>
        </Card>
      </div>
    );
  }

  return (
    <div className="min-h-screen p-4" style={{ background: 'linear-gradient(135deg, #E0F2F1 0%, #B2DFDB 100%)' }}>
      <div className="max-w-3xl mx-auto">
        <Card className="shadow-2xl">
          <div className="h-4" style={{ backgroundColor: '#80CBC4' }} />
          <CardHeader className="text-center">
            <img 
              src="https://customer-assets.emergentagent.com/job_tfs-workspace/artifacts/xuj2h70u_TFSI%203-26%20PNG.png" 
              alt="TFS Logo" 
              className="h-16 mx-auto mb-4"
            />
            <CardTitle className="text-3xl" style={{ fontFamily: 'Space Grotesk, sans-serif' }}>
              POST-SERVICE FEEDBACK
            </CardTitle>
            <CardTitle className="text-2xl text-slate-600" style={{ fontFamily: 'Space Grotesk, sans-serif' }}>
              RETROALIMENTACIÓN DE SERVICIO
            </CardTitle>
            <CardDescription className="text-base mt-4 text-left">
              <p className="mb-2">
                <strong>English:</strong> Thank you for choosing Total Facility Solutions. Please take a moment to provide feedback. 
                Your comments help us recognize what we are doing right and identify opportunities for improvement.
              </p>
              <p>
                <strong>Español:</strong> Gracias por elegir Total Facility Solutions. Por favor tome un momento para darnos su opinión. 
                Sus comentarios nos ayudan a reconocer lo que hacemos bien e identificar oportunidades de mejora.
              </p>
            </CardDescription>
          </CardHeader>
          <CardContent>
            <form onSubmit={handleSubmit} className="space-y-6">
              {/* Service Type */}
              <div className="space-y-2">
                <Label htmlFor="service_type">Service Type / Tipo de Servicio *</Label>
                <select
                  id="service_type"
                  data-testid="feedback-service-type-select"
                  className="w-full border rounded-md px-3 py-2"
                  value={formData.service_type}
                  onChange={(e) => setFormData({ ...formData, service_type: e.target.value })}
                  required
                >
                  <option value="">Select Service / Seleccionar Servicio</option>
                  {serviceTypes.map(type => (
                    <option key={type} value={type}>{type}</option>
                  ))}
                </select>
              </div>

              {/* Customer/Location */}
              <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                <div className="space-y-2">
                  <Label htmlFor="customer">Customer Name / Nombre del Cliente *</Label>
                  <Input
                    id="customer"
                    value={formData.customer_name_location}
                    onChange={(e) => setFormData({ ...formData, customer_name_location: e.target.value })}
                    placeholder="ABC Company"
                    required
                    data-testid="feedback-customer-input"
                  />
                </div>
                <div className="space-y-2">
                  <Label htmlFor="location">Location / Ubicación *</Label>
                  <Input
                    id="location"
                    value={formData.location}
                    onChange={(e) => setFormData({ ...formData, location: e.target.value })}
                    placeholder="Building name or address"
                    required
                    data-testid="feedback-location-input"
                  />
                </div>
              </div>

              {/* Rating */}
              <div className="space-y-3">
                <Label className="text-lg font-semibold">
                  How would you rate your overall service experience?
                </Label>
                <Label className="text-lg font-semibold text-slate-600">
                  ¿Cómo calificaría su experiencia general del servicio?
                </Label>
                
                <div className="grid grid-cols-5 gap-2 md:gap-3 mt-4">
                  {ratingOptions.map((option) => (
                    <button
                      key={option.value}
                      type="button"
                      onClick={() => setFormData({ ...formData, rating: option.value })}
                      className={`p-4 rounded-lg border-2 transition-all hover:scale-105 ${
                        formData.rating === option.value 
                          ? 'ring-4 ring-offset-2' 
                          : 'hover:border-slate-400'
                      }`}
                      style={{ 
                        backgroundColor: formData.rating === option.value ? option.color : 'white',
                        borderColor: option.color,
                        color: formData.rating === option.value ? 'white' : option.color,
                        ringColor: option.color
                      }}
                      data-testid={`rating-${option.value}-btn`}
                    >
                      <div className="text-4xl mb-2">{option.emoji}</div>
                      <div className="text-3xl font-bold mb-1">{option.value}</div>
                      <div className={`text-xs font-medium ${formData.rating === option.value ? 'text-white' : ''}`}>
                        {option.label}
                      </div>
                      <div className={`text-xs ${formData.rating === option.value ? 'text-white' : 'text-slate-600'}`}>
                        {option.labelEs}
                      </div>
                    </button>
                  ))}
                </div>

                {formData.rating > 0 && (
                  <div 
                    className="p-4 rounded-lg text-center font-semibold text-lg mt-4"
                    style={{ 
                      backgroundColor: `${getRatingInfo(formData.rating).color}20`,
                      color: getRatingInfo(formData.rating).color
                    }}
                  >
                    You selected: {getRatingInfo(formData.rating).label} / {getRatingInfo(formData.rating).labelEs}
                  </div>
                )}
              </div>

              {/* Comment Fields */}
              <div className="space-y-2">
                <Label htmlFor="did_well">
                  What did we do well? / ¿Qué hicimos bien? (Optional / Opcional)
                </Label>
                <Textarea
                  id="did_well"
                  value={formData.what_we_did_well}
                  onChange={(e) => setFormData({ ...formData, what_we_did_well: e.target.value })}
                  rows={3}
                  placeholder="Tell us what you appreciated..."
                  data-testid="feedback-did-well-input"
                />
              </div>

              <div className="space-y-2">
                <Label htmlFor="improve">
                  Where can we improve? / ¿Dónde podemos mejorar? (Optional / Opcional)
                </Label>
                <Textarea
                  id="improve"
                  value={formData.where_to_improve}
                  onChange={(e) => setFormData({ ...formData, where_to_improve: e.target.value })}
                  rows={3}
                  placeholder="Share your suggestions..."
                  data-testid="feedback-improve-input"
                />
              </div>

              {/* Optional Customer Info */}
              <div className="border-t pt-6 space-y-4">
                <h3 className="font-semibold text-lg">
                  Contact Information (Optional) / Información de Contacto (Opcional)
                </h3>
                
                <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                  <div className="space-y-2">
                    <Label htmlFor="your_name">Your Name / Su Nombre</Label>
                    <Input
                      id="your_name"
                      value={formData.customer_name}
                      onChange={(e) => setFormData({ ...formData, customer_name: e.target.value })}
                      placeholder="John Doe"
                      data-testid="feedback-your-name-input"
                    />
                  </div>
                  <div className="space-y-2">
                    <Label htmlFor="your_email">Your Email / Su Correo</Label>
                    <Input
                      id="your_email"
                      type="email"
                      value={formData.customer_email}
                      onChange={(e) => setFormData({ ...formData, customer_email: e.target.value })}
                      placeholder="john@email.com"
                      data-testid="feedback-your-email-input"
                    />
                  </div>
                </div>

                <div className="space-y-2">
                  <Label htmlFor="signature">Signature / Firma</Label>
                  <Input
                    id="signature"
                    value={formData.signature}
                    onChange={(e) => setFormData({ ...formData, signature: e.target.value })}
                    placeholder="Type your full name / Escriba su nombre completo"
                    data-testid="feedback-signature-input"
                  />
                </div>
              </div>

              <Button
                type="submit"
                className="w-full text-lg py-6"
                disabled={loading}
                data-testid="feedback-submit-btn"
                style={{ backgroundColor: '#6B9F3E' }}
              >
                <Send className="h-5 w-5 mr-2" />
                {loading ? 'Submitting... / Enviando...' : 'Submit Feedback / Enviar Opinión'}
              </Button>
            </form>
          </CardContent>
        </Card>

        <div className="text-center mt-6 text-sm text-slate-600">
          <p>Total Facility Solutions • admin@tfsi-us.com</p>
        </div>
      </div>
    </div>
  );
};

export default PublicFeedbackForm;
